import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect} from 'react';

import {FetchReviewsResultType, ReviewDataType} from '../../../../service/reviews/reviews-type';
import {getReviewsStatus} from '../../../../service/source-settings/accounts/accounts-api';
import {AccountReviewStatusesEnum} from '../../../../service/source-settings/accounts/accounts-type';

export function useReviewsStatus(reviewsIds: Array<number>, reviewQueryKey: string): null {
    const queryClient = useQueryClient();

    const {data} = useQuery([`useReviewsStatus${reviewsIds}${reviewQueryKey}`], () => getReviewsStatus({reviewsIds}), {
        enabled: Boolean(reviewQueryKey) && reviewsIds?.length > 0,
    });

    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
        queryClient.setQueryData([reviewQueryKey], (oldData?: FetchReviewsResultType) => {
            if (!oldData) {
                return oldData;
            }

            const errorReviewIds = new Set(
                (data || [])
                    .filter((item) => item.accountStatus === AccountReviewStatusesEnum.errorStatus)
                    .flatMap((item) => item.reviewsIds)
            );

            const isSynchronizationIds = new Set(
                (data || [])
                    .filter((item) => item.accountStatus === AccountReviewStatusesEnum.synchronizationStatus)
                    .flatMap((item) => item.reviewsIds)
            );

            if (errorReviewIds.size > 0 || isSynchronizationIds.size > 0) {
                return {
                    ...oldData,
                    results: oldData.results.map((review) => {
                        return isSynchronizationIds.has(review.id)
                            ? {...review, isSynchronizationAccount: true}
                            : errorReviewIds.has(review.id)
                            ? {...review, hasAccountError: true}
                            : review;
                    }),
                };
            }

            return oldData;
        });
    }, [data, queryClient, reviewQueryKey]);

    return null;
}

export function useReviewStatus(reviewQueryKey: string, reviewId?: number): null {
    const queryClient = useQueryClient();

    const {data} = useQuery(
        [`useReviewStatus${reviewId}${reviewQueryKey}`],
        () => getReviewsStatus({reviewsIds: reviewId ? [reviewId] : []}),
        {
            enabled: Boolean(reviewQueryKey) && Boolean(reviewId),
        }
    );

    useEffect(() => {
        queryClient.setQueryData([reviewQueryKey], (oldData?: ReviewDataType) => {
            if (!oldData) {
                return oldData;
            }

            const errorReviewIds = new Set(
                (data || [])
                    .filter((item) => item.accountStatus === AccountReviewStatusesEnum.errorStatus)
                    .flatMap((item) => item.reviewsIds)
            );

            const isSynchronizationIds = new Set(
                (data || [])
                    .filter((item) => item.accountStatus === AccountReviewStatusesEnum.synchronizationStatus)
                    .flatMap((item) => item.reviewsIds)
            );

            const isSynchronizationAccount =
                reviewId && isSynchronizationIds.has(reviewId) ? {isSynchronizationAccount: true} : {};
            const hasAccountError = reviewId && errorReviewIds.has(reviewId) ? {hasAccountError: true} : {};

            return {
                ...oldData,
                ...isSynchronizationAccount,
                ...hasAccountError,
            };
        });
    }, [data, queryClient, reviewId, reviewQueryKey]);

    return null;
}
